import {
  Component,
  OnInit,
  computed,
  signal,
  inject,
  effect,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { injectQuery } from '@tanstack/angular-query-experimental';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

// Kanzi
import * as fromCustomer from '@kanzi-apes/kanzi-customer';
import { CustomerService } from '@kanzi-apes/kanzi-customer';
import * as fromAuth from '@kanzi-apes/kanzi-auth';
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { LocationCreateModel, OperatingHours, CountryFilterModel, StateCountryFilterModel, CityFilterModel, CustomerListModel } from '@kanzi-apes/kanzi-models';
import { Store } from '@ngrx/store';
import { WarehousesQueriesService, UbicacionesQueriesService } from '../../../queries';
import { OperationScheduleComponent } from '../../../components/operation-schedule/operation-schedule.component';

@Component({
    selector: 'kanzi-apes-warehouse-edit',
    imports: [
        CommonModule,
        TranslateModule,
        FormsModule,
        ButtonModule,
        InputTextModule,
        InputTextareaModule,
        DropdownModule,
        MultiSelectModule,
        SkeletonModule,
        ToastModule,
        KanziUiModule,
        OperationScheduleComponent,
    ],
    providers: [MessageService],
    templateUrl: './warehouse-edit.component.html',
    styleUrls: ['./warehouse-edit.component.scss']
})
export class WarehouseEditComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly queries = inject(WarehousesQueriesService);
  private readonly locationQueries = inject(UbicacionesQueriesService);
  private readonly messageService = inject(MessageService);
  private readonly store = inject(Store);
  private readonly customerService = inject(CustomerService);
  userLogged = this.store.selectSignal(fromAuth.getAuthUser);
  customerSelected = this.store.selectSignal(fromCustomer.getCustomerSelected);

  // Determines if customer multi-select should be shown
  // (when logged user's customer has no parent)
  showCustomerSelector = computed(
    () => this.userLogged()?.configs?.customer?.parent == null
  );

  // Query to load available customers (enabled only when multi-select is needed)
  customersQuery = injectQuery<CustomerListModel, Error>(() => ({
    queryKey: ['customers', { page_size: 1000 }],
    queryFn: () =>
      lastValueFrom(this.customerService.listCustomers({ page_size: 1000 })),
    enabled: this.showCustomerSelector(),
    staleTime: 1000 * 60 * 5,
  }));

  // Selected customer IDs for multi-select (plain property for ngModel compatibility)
  selectedCustomerIds: number[] = [];

  // Signals
  warehouseId = signal<number | null>(null);
  isQueryEnabled = signal(false);

  // Query for warehouse detail
  warehouseQuery = this.queries.useWarehouseDetail(
    this.warehouseId,
    this.isQueryEnabled
  );

  // Form data (mutable for ngModel)
  warehouse: LocationCreateModel = {
    name: '',
    display_name: '',
    external_id: '',
    description: '',
    customer_ids: [],
    type_id: undefined,
    country_id: undefined,
    state_id: undefined,
    city_id: undefined,
    address: '',
    postal_code: '',
    phone: '',
    mobile: '',
    operating_hours: undefined,
  };

  // Validation errors
  phoneError = '';
  mobileError = '';
  postalCodeError = '';

  // Computed to check if loading
  isLoading = computed(
    () => this.warehouseQuery.isPending() || this.warehouseQuery.isFetching()
  );

  // Geographic queries
  countriesFilters = signal<CountryFilterModel | null>({ page_size: 1000 });
  countriesQuery = this.locationQueries.useCountries(this.countriesFilters);

  statesFilters = signal<StateCountryFilterModel | null>(null);
  statesQuery = this.locationQueries.useStates(this.statesFilters);

  citiesFilters = signal<CityFilterModel | null>(null);
  citiesQuery = this.locationQueries.useCities(this.citiesFilters);

  // Mutation
  updateMutation = this.queries.useUpdateWarehouse();

  constructor() {
    // Effect to load warehouse data into form
    effect(() => {
      const data = this.warehouseQuery.data();
      if (data) {
        // Extract customer IDs from the location data
        const customerIds = data.customers?.map(c => c.id).filter((id): id is number => id !== undefined) || 
                           data.customer?.map(c => c.id).filter((id): id is number => id !== undefined) ||
                           (data.customer_id ? [data.customer_id] : []);

        // Extract geographic IDs
        const countryId = data.country?.id || data.country_id || undefined;
        const stateId = data.state?.id || data.state_id || undefined;
        const cityId = data.city?.id || data.city_id || undefined;

        this.warehouse = {
          id: data.id,
          name: data.name || '',
          display_name: data.display_name || data.name || '',
          external_id: (data.external_id !== undefined &&
          data.external_id !== null
            ? String(data.external_id)
            : undefined),
          description: data.description || '',
          type_id: data.type?.id,
          customer_ids: customerIds,
          // Geographic information - extract IDs from nested objects or use direct IDs
          country_id: countryId,
          state_id: stateId,
          city_id: cityId,
          address: data.address || '',
          postal_code: data.postal_code || '',
          // Contact information
          phone: data.phone || '',
          mobile: data.mobile || '',
          // Operation schedules
          operating_hours: data.operating_hours || undefined,
        };

        // Pre-populate multi-select with existing customer IDs
        this.selectedCustomerIds = [...customerIds];

        // Load states if country is selected - use untracked to avoid triggering effect again
        if (countryId) {
          untracked(() => {
            this.statesFilters.set({
              country_id: countryId,
              page_size: 1000,
            });
          });
        }

        // Load cities if state is selected - use untracked to avoid triggering effect again
        if (stateId) {
          untracked(() => {
            this.citiesFilters.set({
              state_id: stateId,
              page_size: 1000,
            });
          });
        }
      }
    });
  }

  ngOnInit(): void {
    // Get ID from route
    this.route.params.subscribe((params) => {
      const id = Number(params['id']);
      if (id) {
        this.warehouseId.set(id);
        this.isQueryEnabled.set(true);
      }
    });
  }

  /**
   * Handle operating hours changes
   */
  onSchedulesChange(operatingHours: OperatingHours): void {
    this.warehouse.operating_hours = operatingHours;
  }

  /**
   * Handle country change
   */
  onCountryChange(): void {
    // Reset state and city when country changes
    this.warehouse.state_id = undefined;
    this.warehouse.city_id = undefined;
    
    // Update states filter
    if (this.warehouse.country_id) {
      this.statesFilters.set({ 
        country_id: this.warehouse.country_id,
        page_size: 1000 
      });
    } else {
      this.statesFilters.set(null);
    }
    
    // Reset cities filter
    this.citiesFilters.set(null);
  }

  /**
   * Handle state change
   */
  onStateChange(): void {
    // Reset city when state changes
    this.warehouse.city_id = undefined;
    
    // Update cities filter
    if (this.warehouse.state_id) {
      this.citiesFilters.set({ 
        state_id: this.warehouse.state_id,
        page_size: 1000 
      });
    } else {
      this.citiesFilters.set(null);
    }
  }

  /**
   * Validate phone format: digits, +, -, spaces. Min 7, max 20 characters
   */
  validatePhone(): void {
    if (this.warehouse.phone) {
      const phone = this.warehouse.phone.trim();
      const phoneRegex = /^[\d\s\-+]+$/;
      
      if (!phoneRegex.test(phone)) {
        this.phoneError = 'El teléfono solo puede contener dígitos, +, -, y espacios';
      } else if (phone.length < 7 || phone.length > 20) {
        this.phoneError = 'El teléfono debe tener entre 7 y 20 caracteres';
      } else {
        this.phoneError = '';
      }
    } else {
      this.phoneError = '';
    }
  }

  /**
   * Validate mobile format: digits, +, -, spaces. Min 7, max 20 characters
   */
  validateMobile(): void {
    if (this.warehouse.mobile) {
      const mobile = this.warehouse.mobile.trim();
      const phoneRegex = /^[\d\s\-+]+$/;
      
      if (!phoneRegex.test(mobile)) {
        this.mobileError = 'El celular solo puede contener dígitos, +, -, y espacios';
      } else if (mobile.length < 7 || mobile.length > 20) {
        this.mobileError = 'El celular debe tener entre 7 y 20 caracteres';
      } else {
        this.mobileError = '';
      }
    } else {
      this.mobileError = '';
    }
  }

  /**
   * Validate postal code
   */
  validatePostalCode(): void {
    if (this.warehouse.postal_code && this.warehouse.postal_code.length > 10) {
      this.postalCodeError = 'El código postal no puede tener más de 10 caracteres';
    } else {
      this.postalCodeError = '';
    }
  }

  /**
   * Check if form has validation errors
   */
  hasValidationErrors(): boolean {
    this.validatePhone();
    this.validateMobile();
    this.validatePostalCode();
    return !!(this.phoneError || this.mobileError || this.postalCodeError);
  }

  /**
   * Save changes
   */
  onSave(): void {
    // Basic validation
    if (!this.warehouse.name?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'El nombre del warehouse es requerido',
      });
      return;
    }

    // Validate phones and postal code
    this.validatePhone();
    this.validateMobile();
    this.validatePostalCode();

    if (this.phoneError || this.mobileError || this.postalCodeError) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Por favor corrija los errores en el formulario',
      });
      return;
    }

    if (!this.warehouse.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'ID del warehouse no encontrado',
      });
      return;
    }

    // Validate at least one customer is selected when multi-select is active
    if (this.showCustomerSelector() && this.selectedCustomerIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Debe seleccionar al menos un cliente',
      });
      return;
    }

    const dataToSave: LocationCreateModel = {
      ...this.warehouse,
      customer_ids: this.showCustomerSelector()
        ? this.selectedCustomerIds
        : this.warehouse.customer_ids,
    };

    this.updateMutation.mutate(dataToSave, {
      onSuccess: (data) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Warehouse actualizado',
          detail: `El warehouse "${data.name}" fue actualizado exitosamente`,
        });

        // Navigate to detail
        if (data.id) {
          this.router.navigate(['locations', 'warehouses', 'detail', data.id]);
        }
      },
      onError: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error al actualizar',
          detail:
            error.message || 'Ocurrió un error al actualizar el warehouse',
        });
      },
    });
  }

  /**
   * Cancel and return to detail
   */
  onCancel(): void {
    const id = this.warehouseId();
    if (id) {
      this.router.navigate(['locations', 'warehouses', 'detail', id]);
    } else {
      this.router.navigate([
        'configurations',
        'location',
        'warehouses',
        'list',
      ]);
    }
  }
}
